'use client';

import { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react';
import cn from 'classnames';
import styles from './scrollablechanneltiles.module.scss';
import '@splidejs/react-splide/css';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import type { Splide as SplideClass } from '@splidejs/splide';
import { ChevronIcon } from '@/assets/icons/ChevronIcon';
import type { Options as SplideOptions } from '@splidejs/splide';

export interface ImageItem {
    content: ReactNode;
}

export type ArrowVariant = 'below' | 'overlay';

interface TrackEnds {
    atStart: boolean;
    atEnd: boolean;
}

const INITIAL_TRACK_ENDS: TrackEnds = { atStart: true, atEnd: false };

const POSITION_EPSILON = 1;

function readTrackEnds(splide: SplideClass, position: number): TrackEnds {
    const { Move } = splide.Components;
    return {
        atStart: Math.abs(position - Move.getLimit(false)) < POSITION_EPSILON,
        atEnd: Math.abs(position - Move.getLimit(true)) < POSITION_EPSILON,
    };
}

export interface ScrollableChannelTilesProps {
    items: ImageItem[];
    extraTileColor?: string;
    extraTileLabel?: string;
    handleViewMoreClick?: () => void;
    scrolledToEnd?: boolean;
    arrowVariant?: ArrowVariant;
    isScrollableChannelTiles?: boolean;
}

export interface ScrollableChannelTilesHandle {
    animateOneTile: () => void;
}
type StylesProps = React.CSSProperties & {
    '--tile-background': string;
};
export const ScrollableChannelTiles = forwardRef<ScrollableChannelTilesHandle, ScrollableChannelTilesProps>(
    (
        {
            items,
            extraTileColor,
            extraTileLabel,
            handleViewMoreClick,
            scrolledToEnd,
            arrowVariant = 'below',
            isScrollableChannelTiles = true,
        },
        ref,
    ) => {
        const splideRef = useRef<SplideClass | null>(null);
        const [activeIndex, setActiveIndex] = useState(-1);
        const [trackEnds, setTrackEnds] = useState<TrackEnds>(INITIAL_TRACK_ENDS);
        const listRef = useRef<HTMLDivElement | null>(null);
        const isAnimatingRef = useRef(false);

        const isOverlay = arrowVariant === 'overlay';
        const prevDisabled = isOverlay ? trackEnds.atStart : activeIndex <= 0;
        const nextDisabled = isOverlay ? trackEnds.atEnd : activeIndex >= items?.length;

        const syncTrackEnds = (splide: SplideClass) => {
            setTrackEnds(readTrackEnds(splide, splide.Components.Move.getPosition()));
        };

        function scrollToEndImmediately() {
            if (!splideRef.current) return;
            const perPage = splideRef.current.options.perPage ?? 1;
            const endIndex = splideRef.current.length - perPage;
            const currentOptions = { ...splideRef.current.options };
            splideRef.current.options = {
                ...currentOptions,
                speed: 0,
            };
            splideRef.current.go(endIndex);
            setTimeout(() => {
                if (!splideRef.current) return;
                splideRef.current.options = {
                    ...currentOptions,
                };
            }, 0);
        }

        const getTranslateX = (element: HTMLElement): number => {
            const style = window.getComputedStyle(element);
            const matrix = new WebKitCSSMatrix(style.transform);
            return matrix.m41;
        };

        function animateOneTile() {
            if (isAnimatingRef.current || !listRef.current) return;
            const list = listRef.current;
            const originalX = getTranslateX(list);
            const shiftAmount = 20;
            isAnimatingRef.current = true;
            list.style.transition = 'transform 0.4s ease';
            list.style.transform = `translateX(${originalX - shiftAmount}px)`;
            setTimeout(() => {
                list.style.transform = `translateX(${originalX}px)`;
            }, 400);
            setTimeout(() => {
                list.style.transition = '';
                isAnimatingRef.current = false;
            }, 800);
        }

        useImperativeHandle(ref, () => ({
            animateOneTile,
        }));

        useEffect(() => {
            if (!scrolledToEnd) return;

            const timeout = setTimeout(() => {
                scrollToEndImmediately();
            }, 100);
            return () => clearTimeout(timeout);
        }, [scrolledToEnd]);

        const splideOptions: SplideOptions = {
            type: 'slide',
            gap: isOverlay ? '8px' : '12px',
            pagination: false,
            arrows: false,
            speed: 1000,
            easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
            padding: { left: '24px', right: '24px' },
        };

        const lastTileStyles: React.CSSProperties = {
            '--tile-background': extraTileColor,
        } as StylesProps;

        const scrollToIndexWithAdjustedSpeed = (targetIndex: number) => {
            if (!splideRef.current) return;
            const currentIndex = splideRef.current.index;
            const tilesToScroll = Math.abs(targetIndex - currentIndex);
            const speed = Math.min(tilesToScroll * 300, 10000);
            splideRef.current.options = {
                ...splideRef.current.options,
                speed,
            };
            splideRef.current.go(targetIndex);
        };

        const handleArrowClick = (direction: 'start' | 'end') => {
            if (splideRef.current) {
                const perPage = splideRef.current.options.perPage ?? 1;
                const endIndex = splideRef.current.length - perPage;
                const targetIndex = direction === 'start' ? 0 : endIndex;
                scrollToIndexWithAdjustedSpeed(targetIndex);
            }
        };

        const handleViewMoreTileClick = () => {
            if (handleViewMoreClick) {
                handleViewMoreClick();
            }
        };

        return isScrollableChannelTiles ? (
            <div className={cn(styles.carouselContainer, { [styles.carouselContainerOverlay]: isOverlay })}>
                <Splide
                    options={splideOptions}
                    ref={(instance) => {
                        splideRef.current = instance?.splide || null;
                        setTimeout(() => {
                            if (instance?.splide?.root) {
                                const list = instance.splide.root.querySelector('.splide__list') as HTMLDivElement;
                                if (list) {
                                    listRef.current = list;
                                }
                            }
                        }, 0);
                    }}
                    onMounted={syncTrackEnds}
                    onMove={(splide, newIndex) => {
                        setActiveIndex(newIndex);
                        setTrackEnds(readTrackEnds(splide, splide.Components.Move.toPosition(newIndex, true)));
                    }}
                    onMoved={syncTrackEnds}
                    onDragged={syncTrackEnds}
                    onScrolled={syncTrackEnds}
                    onResized={syncTrackEnds}
                    className={styles.splide}
                >
                    {items.map((item, index) => (
                        <SplideSlide key={index} className={styles.slide}>
                            <div className={styles.slideTiles}>{item?.content}</div>
                        </SplideSlide>
                    ))}
                    {arrowVariant === 'below' && (
                        <SplideSlide className={styles.slide}>
                            <div className={styles.lastSlideTile} style={lastTileStyles}>
                                <button
                                    className={styles.viewMore}
                                    onClick={handleViewMoreTileClick}
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleViewMoreTileClick();
                                        }
                                    }}
                                    onFocus={() => {
                                        scrollToEndImmediately();
                                    }}
                                    aria-label={extraTileLabel}
                                >
                                    <span>{extraTileLabel}</span>
                                    <ChevronIcon direction={'down'} />
                                </button>
                            </div>
                        </SplideSlide>
                    )}
                </Splide>
                <div className={styles.carouselArrows}>
                    <button
                        className={styles.leftArrow}
                        aria-label={`scroll left`}
                        onClick={() => handleArrowClick('start')}
                        disabled={prevDisabled}
                    >
                        <ChevronIcon />
                    </button>
                    <button
                        className={styles.rightArrow}
                        aria-label={`scroll right`}
                        onClick={() => handleArrowClick('end')}
                        disabled={nextDisabled}
                    >
                        <ChevronIcon />
                    </button>
                </div>
            </div>
        ) : (
            <div className={styles.gridContainer}>
                {items.map((item, index) => (
                    <div key={index} className={styles.slideTiles}>
                        {item?.content}
                    </div>
                ))}
            </div>
        );
    },
);

ScrollableChannelTiles.displayName = 'ScrollableChannelTiles';
